import React, { useState, useMemo } from "react";
import {
  Dialog,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
  InputAdornment,
  IconButton,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { AddAdminDialogStyle as styles } from "./AddAdminDialogStyle";
import { useRecoilValue } from "recoil";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import { loginUserAtom } from "../recoil/atoms/loginUserAtoms";
import BaseFilterSelect from "./BaseFilter";
import {
  RequestCheckDuplicateEmailAddressRequest,
  RequestCreateMstUsersRequest,
} from "../services/OpenAPI";
import { useApiRegisterUser } from "../hooks/api/useApiRegisterUser";
import { useApiCheckDuplicateMailAddress } from "../hooks/api/useApiCheckDuplicateMailAddress";
import { Permission } from "../constants/roles";

type Props = {
  open: boolean;
  onClose: () => void;
  onResister: (newUser: RequestCreateMstUsersRequest) => void;
};

const AddAdminDialog: React.FC<Props> = ({ open, onClose, onResister }) => {
  const loginUser = useRecoilValue(loginUserAtom);
  const { checkDuplicateMailAddress } = useApiCheckDuplicateMailAddress();

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );

  const [step, setStep] = useState(1);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mail, setMailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lineName, setLineName] = useState("");

  const [baseName, setBaseName] = useState("");
  const [selectedBaseId, setSelectedBaseId] = useState<number | null>(null);

  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  const resetFields = () => {
    setFirstName("");
    setLastName("");
    setMailAddress("");
    setPassword("");
    setConfirmPassword("");
    setLineName("");
    setBaseName("");
    setSelectedBaseId(null);
    setStep(1);
  };

  const generatePassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";
    const getRandomChar = (chars: string) =>
      chars[Math.floor(Math.random() * chars.length)];

    const requiredChars = [
      getRandomChar(upper),
      getRandomChar(lower),
      getRandomChar(numbers),
      getRandomChar(symbols),
    ];

    const all = upper + lower + numbers + symbols;
    for (let i = requiredChars.length; i < 10; i++) {
      requiredChars.push(getRandomChar(all));
    }

    return requiredChars.sort(() => 0.5 - Math.random()).join("");
  };

  const isEmpty = (v: string | undefined | null) => !v || v.trim() === "";

  const validateCheck = () => {
    return (
      isEmpty(firstName) ||
      isEmpty(lastName) ||
      isEmpty(password) ||
      password.length < 8 ||
      isEmpty(mail) ||
      selectedBaseId === null
    );
  };

  const { registerUser } = useApiRegisterUser();

  const handleRegister = async () => {
    if (!loginUser.user?.id || selectedBaseId === null) {
      setToastMessage("登録に必要な情報が不足しています");
      setToastSeverity("error");
      setToastOpen(true);
      return;
    }

    const checkReq: RequestCheckDuplicateEmailAddressRequest = {
      mailAddress: mail,
    };
    const result = await checkDuplicateMailAddress(checkReq);
    if (!result) {
      setToastMessage("登録されているメールアドレスです。");
      setToastSeverity("error");
      setToastOpen(true);
      return;
    }

    const newAdmin: RequestCreateMstUsersRequest = {
      tenantId: loginUser.user.tenantId ?? 1,
      createdUserId: loginUser.user.id,
      baseId: selectedBaseId,
      firstName,
      lastName,
      mail,
      permissionId: Permission.BaseAdmin,
      password,
      lineName,
      approval: true,
    };

    try {
      await registerUser(newAdmin);
      onResister(newAdmin);
      resetFields();
      setToastMessage("管理者を登録しました");
      setToastSeverity("success");
      setToastOpen(true);
    } catch (e) {
      console.error("登録エラー", e);
      setToastMessage("登録に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") onClose();
        }}
        disableEnforceFocus
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            display: "flex",
            width: "800px",
            height: "600px",
            maxWidth: "none",
            maxHeight: "none",
            overflowY: "auto",
          },
        }}
      >
        <Box sx={styles.header}>
          <Typography sx={styles.headerText}>新規管理者登録</Typography>
          <Button
            onClick={() => {
              resetFields();
              onClose();
            }}
            sx={styles.closeButton}
          >
            ×
          </Button>
        </Box>

        <Box sx={styles.formArea}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <TextField
              label="姓"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              margin="normal"
              sx={{ width: "60%" }}
            />
            <TextField
              label="名"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              margin="normal"
              sx={{ width: "60%" }}
            />
            <TextField
              label="メールアドレス"
              value={mail}
              onChange={(e) => setMailAddress(e.target.value)}
              fullWidth
              margin="normal"
              sx={{ width: "60%" }}
            />
            <TextField
              label="パスワード"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              sx={{ width: "60%" }}
              InputProps={{
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() => {
                          const newPass = generatePassword();
                          setPassword(newPass);
                        }}
                      >
                        自動生成
                      </Button>
                    </InputAdornment>
                  </>
                ),
              }}
            />
            <BaseFilterSelect
              options={baseOptions}
              values={selectedBaseId !== null ? [selectedBaseId] : []}
              onSelect={(id: number | null) => {
                setSelectedBaseId(id);
                const base = baseOptions.find((b) => b.id === id);
                setBaseName(base?.label ?? "");
              }}
              isMultiple={false}
              sx={{ width: "60%", mt: 2 }}
            />
          </Box>
        </Box>

        <Button
          onClick={handleRegister}
          variant="contained"
          sx={styles.floatingRegisterButton}
          disabled={validateCheck()}
        >
          登録
        </Button>

        <Snackbar
          open={toastOpen}
          autoHideDuration={4000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setToastOpen(false)}
            severity={toastSeverity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {toastMessage}
          </MuiAlert>
        </Snackbar>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddAdminDialog;
